<!--
  登录面板组件 - 纯内容，无弹窗包装
  可嵌入 el-dialog 或 el-popover 中使用
-->
<template>
  <div class="login-panel">
    <div class="dialog-logo">
      <div class="logo-text">文字转语音</div>
    </div>

    <!-- 登录方式切换 -->
    <div class="login-mode-tabs">
      <TButton
        :variant="loginMode === 'wechat' ? 'primary' : 'secondary'"
        @click="switchLoginMode('wechat')"
        size="small"
      >
        微信扫码，立即登录
      </TButton>
      <TButton
        :variant="loginMode === 'email' ? 'primary' : 'secondary'"
        @click="switchLoginMode('email')"
        size="small"
      >
        账号登录
      </TButton>
    </div>

    <!-- 微信扫码登录 -->
    <div v-if="loginMode === 'wechat'" class="wechat-login-container">
      <WechatLogin
        :on-success="handleWechatLoginSuccess"
        :on-error="handleWechatLoginError"
        @switch-to-email="switchLoginMode('email')"
        ref="wechatLoginRef"
      />
      <div class="login-mode-switch">
        <el-text size="small" type="info">
          绑定邮箱后可使用账号登录
        </el-text>
      </div>
    </div>

    <!-- 邮箱登录表单 -->
    <el-form
      v-else-if="loginMode === 'email' && isLoginMode"
      id="loginForm"
      ref="loginFormRef"
      :model="loginForm"
      :rules="loginRules"
      label-position="top"
    >
      <el-form-item label="账号" prop="email">
        <TInput
          v-model="loginForm.email"
          placeholder="请输入电子邮箱/用户名"
        />
      </el-form-item>
      <el-form-item label="密码" prop="password">
        <TInput
          v-model="loginForm.password"
          type="password"
          placeholder="请输入密码"
          show-password
        />
      </el-form-item>

      <div v-if="loginError" class="login-error-message">
        <el-alert
          :title="loginError"
          type="error"
          :closable="false"
          show-icon
        />
      </div>

      <div class="form-actions">
        <TButton variant="primary" :loading="loading" @click="handleLogin" style="width: 100%">
          立即登录
        </TButton>
      </div>
    </el-form>

    <!-- 切换到微信登录 -->
    <div class="mode-switch" v-if="loginMode === 'email'">
      <TButton variant="text" @click="switchLoginMode('wechat')">
        没有账户？使用微信登录注册
      </TButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/store'
import WechatLogin from '@/views/auth/WechatLogin.vue'
import { TButton, TInput } from '@/components/ui'

const emit = defineEmits<{
  (e: 'login-success', data: any): void
  (e: 'register-success', data: any): void
  (e: 'close'): void
}>()

const userStore = useUserStore()

const loginMode = ref<'email' | 'wechat'>('wechat')
const isLoginMode = ref(true)
const loading = ref(false)
const loginError = ref('')

const loginFormRef = ref<any>()
const wechatLoginRef = ref<any>()

const loginForm = reactive({
  email: '',
  password: ''
})

const loginRules = {
  email: [{ required: true, message: '请输入邮箱或用户名', trigger: 'blur' }],
  password: [{ required: true, message: '请输入密码', trigger: 'blur' }]
}

const switchLoginMode = (mode: 'email' | 'wechat') => {
  if (loginMode.value === 'wechat' && mode === 'email') {
    wechatLoginRef.value?.cleanup?.()
  }
  loginMode.value = mode
  loginError.value = ''
}

const handleLogin = async () => {
  if (!loginFormRef.value) return
  try {
    await loginFormRef.value.validate()
    loading.value = true
    loginError.value = ''
    await userStore.login({ email: loginForm.email, password: loginForm.password })
    wechatLoginRef.value?.cleanup?.()
    emit('login-success', { ...loginForm })
    emit('close')
  } catch (error: any) {
    const errorMsg = error.message || '登录失败，请检查账号密码'
    if (errorMsg.includes('未绑定邮箱') || errorMsg.includes('邮箱未绑定')) {
      loginError.value = '该账号未绑定邮箱，请使用微信登录后在账户设置中绑定邮箱'
    } else {
      loginError.value = errorMsg
    }
  } finally {
    loading.value = false
  }
}

const handleWechatLoginSuccess = async (data: any) => {
  try {
    await userStore.handleWechatLoginSuccess(data)
    ElMessage.success('微信登录成功')
    emit('login-success', data)
    emit('close')
  } catch {
    ElMessage.success('微信登录成功')
    emit('login-success', data)
    emit('close')
  }
}

const handleWechatLoginError = (error: any) => {
  ElMessage.error('微信登录失败')
  console.error('Wechat login error:', error)
}

// 暴露给父组件重置微信登录状态
const resetWechat = async () => {
  if (loginMode.value === 'wechat') {
    await nextTick()
    wechatLoginRef.value?.reset?.()
  }
}

const cleanupWechat = async () => {
  if (loginMode.value === 'wechat') {
    await nextTick()
    wechatLoginRef.value?.cleanup?.()
  }
}

defineExpose({ resetWechat, cleanupWechat })
</script>

<style scoped>
.login-panel {
  padding: var(--tts-space-2) 0;
}

.dialog-logo {
  text-align: center;
  margin-bottom: var(--tts-space-5);
}

.logo-text {
  font-size: var(--tts-font-xl);
  font-weight: var(--tts-font-bold);
  color: var(--tts-primary);
}

.login-mode-tabs {
  display: flex;
  justify-content: center;
  gap: var(--tts-space-3);
  margin-bottom: var(--tts-space-5);
}

.wechat-login-container {
  text-align: center;
}

.login-mode-switch {
  margin-top: var(--tts-space-4);
}

.login-error-message {
  margin-bottom: var(--tts-space-4);
}

.form-actions {
  margin-top: var(--tts-space-5);
}

.mode-switch {
  text-align: center;
  margin-top: var(--tts-space-4);
  padding-top: var(--tts-space-4);
  border-top: 1px solid var(--tts-border-light);
}
</style>
